/**
 * Shared pagination types and utilities
 */

// Default page size
export const DEFAULT_PAGE_SIZE = 50

// Pagination state for components
export interface PaginationState {
  page: number
  total: number
  totalPages: number
  hasMore: boolean
  perPage: number
}

// API response format from BE
export interface PaginationResponse {
  total: number
  page: number
  per_page: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

// Paginated API response wrapper
export interface PaginatedResponse<T> {
  data: T[]
  pagination: PaginationResponse
}

// Default pagination state
export const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  total: 0,
  totalPages: 0,
  hasMore: false,
  perPage: DEFAULT_PAGE_SIZE
}

/**
 * Parse API pagination response to component state
 */
export function parsePaginationResponse(pagination: PaginationResponse | null): PaginationState {
  if (!pagination) return { ...DEFAULT_PAGINATION }
  return {
    page: pagination.page,
    total: pagination.total,
    totalPages: pagination.total_pages,
    hasMore: pagination.page < pagination.total_pages,
    perPage: pagination.per_page || DEFAULT_PAGE_SIZE
  }
}

/**
 * Check if response is paginated format
 */
export function isPaginatedResponse<T>(response: any): response is PaginatedResponse<T> {
  return response && typeof response === 'object' && 'pagination' in response && 'data' in response
}

/**
 * Parse API response that may or may not be paginated
 * Returns normalized data and pagination state
 */
export function parseApiResponse<T, R = T>(
  response: T[] | PaginatedResponse<T>,
  mapper?: (item: T) => R
): { data: R[], pagination: PaginationState } {
  const isPaginated = isPaginatedResponse(response)
  const rawData = isPaginated ? response.data : response
  const pagination = isPaginated ? parsePaginationResponse(response.pagination) : { ...DEFAULT_PAGINATION }

  // If no mapper, just cast (assuming T = R)
  const data = mapper
    ? rawData.map(mapper)
    : rawData as unknown as R[]

  // Update pagination total if not paginated (backward compat)
  if (!isPaginated) {
    pagination.total = data.length
    pagination.totalPages = 1
    pagination.perPage = data.length || DEFAULT_PAGE_SIZE
  }

  return { data, pagination }
}
